import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Plus,
  ArrowLeft,
  Loader2,
  TicketIcon,
  Send,
  Eye,
  Clock,
  User,
  AlertCircle,
  Search,
  Filter,
  MessageSquare,
  Bug,
  Lightbulb,
  HelpCircle,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import { apiClient } from '@/lib/api';

interface Ticket {
  id: string;
  subject: string;
  description: string;
  urgency: string;
  category?: string;
  status_name: string;
  status_color?: string;
  status_is_closed?: boolean;
  url_path?: string;
  assigned_to_name?: string;
  creator_name?: string;
  created_at: string;
  updated_at?: string;
  first_response_at?: string;
  resolved_at?: string;
}

interface Note {
  id: string;
  content: string;
  author_name?: string;
  is_private: boolean;
  created_at: string;
}

const URGENCY_CONFIG: Record<string, { label: string; className: string }> = {
  low: { label: 'Bassa', className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  medium: { label: 'Media', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  high: { label: 'Alta', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  critical: { label: 'Critica', className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

const CATEGORY_CONFIG: Record<string, { label: string; icon: LucideIcon; className: string }> = {
  bug: { label: 'Bug', icon: Bug, className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  feature_request: { label: 'Richiesta Funzionalità', icon: Lightbulb, className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  question: { label: 'Domanda', icon: HelpCircle, className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  operational: { label: 'Problema Operativo', icon: Wrench, className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
};

const STATUS_DOT_COLORS: Record<string, string> = {
  open: 'bg-blue-500',
  in_progress: 'bg-yellow-500',
  waiting: 'bg-orange-500',
  resolved: 'bg-green-500',
  closed: 'bg-gray-500',
};

function getStatusDotColor(status: string, statusColor?: string): string {
  if (statusColor) return '';
  return STATUS_DOT_COLORS[status] || 'bg-gray-400';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}


export default function SupportTickets() {
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();

  const [urlPathOverride, setUrlPathOverride] = useState(window.location.pathname);

  const [view, setView] = useState<'list' | 'detail'>('list');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  const [newSubject, setNewSubject] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newUrgency, setNewUrgency] = useState('low');
  const [newCategory, setNewCategory] = useState('bug');
  const [noteContent, setNoteContent] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterUrgency, setFilterUrgency] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`/support`);
      if (data.success) {
        setTickets((data.data as Ticket[] | undefined) ?? []);
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile caricare i ticket', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadTicketDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const data = await apiClient.get(`/support/${id}`);
      if (data.success && data.data) {
        const { notes, ...ticketData } = data.data as Ticket & { notes?: Note[] };
        setSelectedTicket(ticketData);
        setNotes((notes ?? []).filter((n) => !n.is_private));
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile caricare il dettaglio', variant: 'destructive' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newSubject.trim() || !newDescription.trim()) {
      toast({ title: 'Errore', description: 'Compila tutti i campi obbligatori', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const data = await apiClient.post(`/support`, {
        subject: newSubject.trim(),
        description: newDescription.trim(),
        urgency: newUrgency,
        category: newCategory,
        url_path: urlPathOverride || window.location.pathname,
      });
      if (data.success) {
        toast({ title: 'Successo', description: 'Ticket creato con successo' });
        setDialogOpen(false);
        setNewSubject('');
        setNewDescription('');
        setNewUrgency('low');
        setNewCategory('bug');
        loadTickets();
      } else {
        toast({ title: 'Errore', description: data.error || 'Errore nella creazione', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile creare il ticket', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !selectedTicket) return;
    setAddingNote(true);
    try {
      const data = await apiClient.post(`/support/${selectedTicket.id}/notes`, {
        content: noteContent.trim(),
      });
      if (data.success) {
        toast({ title: 'Successo', description: 'Nota aggiunta' });
        setNoteContent('');
        loadTicketDetail(selectedTicket.id);
      } else {
        toast({ title: 'Errore', description: data.error || 'Errore', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile aggiungere la nota', variant: 'destructive' });
    } finally {
      setAddingNote(false);
    }
  };

  const openDetail = (ticket: Ticket) => {
    setView('detail');
    loadTicketDetail(ticket.id);
  };

  const filteredTickets = useMemo(() => {
    let result = tickets;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => t.subject.toLowerCase().includes(q));
    }
    if (filterUrgency) result = result.filter(t => t.urgency === filterUrgency);
    if (filterStatus) result = result.filter(t => t.status_name?.toLowerCase() === filterStatus.toLowerCase());
    if (filterCategory) result = result.filter(t => t.category === filterCategory);
    return result;
  }, [tickets, searchQuery, filterUrgency, filterStatus, filterCategory]);

  const openTicketCount = useMemo(() => tickets.filter(t => !t.status_is_closed).length, [tickets]);

  useEffect(() => {
    loadTickets();
  }, [tenantSlug, loadTickets]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (view === 'detail') {
    return (
      <div className="p-6 w-full">
        <Button
          variant="ghost"
          onClick={() => { setView('list'); setSelectedTicket(null); setNotes([]); }}
          className="mb-4 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alla lista
        </Button>

        {detailLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : selectedTicket ? (
          <div className="space-y-6">
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl border border-white/20 dark:border-gray-700/50 p-6 shadow-lg">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedTicket.subject}</h2>
                <div className="flex items-center gap-2">
                  {selectedTicket.category && CATEGORY_CONFIG[selectedTicket.category] && (
                    <Badge className={CATEGORY_CONFIG[selectedTicket.category]!.className!}>
                      {CATEGORY_CONFIG[selectedTicket.category]!.label!}
                    </Badge>
                  )}
                  <Badge className={URGENCY_CONFIG[selectedTicket.urgency]?.className || 'bg-gray-100 text-gray-700'}>
                    {URGENCY_CONFIG[selectedTicket.urgency]?.label || selectedTicket.urgency}
                  </Badge>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: selectedTicket.status_color || '#6B7280' }}
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-300">{selectedTicket.status_name}</span>
                  </div>
                </div>
              </div>

              <div className="prose dark:prose-invert max-w-none mb-4">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span>Creato: {formatDate(selectedTicket.created_at)}</span>
                </div>
                {selectedTicket.assigned_to_name && (
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <User className="w-4 h-4" />
                    <span>Assegnato a: {selectedTicket.assigned_to_name}</span>
                  </div>
                )}
                {selectedTicket.url_path && (
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="truncate">Pagina: {selectedTicket.url_path}</span>
                  </div>
                )}
                {selectedTicket.first_response_at && (
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <MessageSquare className="w-4 h-4" />
                    <span>Risposta: {formatDate(selectedTicket.first_response_at)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl border border-white/20 dark:border-gray-700/50 p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cronologia Note</h3>

              {notes.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm italic">Nessuna nota pubblica disponibile.</p>
              ) : (
                <div className="space-y-4">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="relative pl-6 border-l-2 border-purple-200 dark:border-purple-700"
                    >
                      <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-purple-500" />
                      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {note.author_name || 'Sistema'}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(note.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-3">
                  <Textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Scrivi una nota..."
                    className="flex-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
                    rows={3}
                  />
                  <Button
                    onClick={handleAddNote}
                    disabled={addingNote || !noteContent.trim()}
                    className="self-end bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">Ticket non trovato.</p>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">I Miei Ticket di Supporto</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Gestisci le tue richieste di assistenza</p>
          </div>
          {openTicketCount > 0 && (
            <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 text-sm px-2.5 py-1">
              {openTicketCount} {openTicketCount === 1 ? 'aperto' : 'aperti'}
            </Badge>
          )}
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">Nuovo Ticket di Supporto</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Oggetto *</label>
                <Input
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  placeholder="Descrivi brevemente il problema"
                  className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrizione *</label>
                <Textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Fornisci tutti i dettagli utili"
                  rows={5}
                  className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categoria</label>
                  <Select value={newCategory} onValueChange={setNewCategory}>
                    <SelectTrigger className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Urgenza</label>
                  <Select value={newUrgency} onValueChange={setNewUrgency}>
                    <SelectTrigger className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Bassa</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="critical">Critica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pagina di riferimento</label>
                <Input
                  value={urlPathOverride}
                  onChange={(e) => setUrlPathOverride(e.target.value)}
                  placeholder="/pagina"
                  className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
                />
              </div>
              <Button
                onClick={handleCreate}
                disabled={creating || !newSubject.trim() || !newDescription.trim()}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Invia Ticket
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {tickets.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca per oggetto..."
              className="pl-9 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
            />
          </div>
          <Select value={filterUrgency} onValueChange={val => setFilterUrgency(val === '__all__' ? '' : val)}>
            <SelectTrigger className="w-[140px] bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
              <SelectValue placeholder="Urgenza" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              {Object.entries(URGENCY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={val => setFilterCategory(val === '__all__' ? '' : val)}>
            <SelectTrigger className="w-[160px] bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={val => setFilterStatus(val === '__all__' ? '' : val)}>
            <SelectTrigger className="w-[150px] bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti</SelectItem>
              {[...new Set(tickets.map(t => t.status_name))].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl border border-white/20 dark:border-gray-700/50 shadow-lg">
          <TicketIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-300 mb-2">Nessun ticket</h3>
          <p className="text-gray-400 dark:text-gray-500 mb-6">Non hai ancora creato nessun ticket di supporto.</p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crea il tuo primo ticket
          </Button>
        </div>
      ) : (
        <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-xl border border-white/20 dark:border-gray-700/50 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Oggetto</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Categoria</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Urgenza</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stato</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Risposta</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTickets.map((ticket) => {
                  const catCfg = CATEGORY_CONFIG[ticket.category || ''];
                  return (
                    <tr
                      key={ticket.id}
                      className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                      onClick={() => openDetail(ticket)}
                    >
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{ticket.subject}</span>
                      </td>
                      <td className="px-6 py-4">
                        {catCfg ? (
                          <Badge className={catCfg.className}>{catCfg.label}</Badge>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={URGENCY_CONFIG[ticket.urgency]?.className || 'bg-gray-100 text-gray-700'}>
                          {URGENCY_CONFIG[ticket.urgency]?.label || ticket.urgency}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: ticket.status_color || '#6B7280' }}
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-300">{ticket.status_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(ticket.created_at)}</span>
                      </td>
                      <td className="px-6 py-4">
                        {ticket.first_response_at ? (
                          <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <MessageSquare className="w-3.5 h-3.5" />
                            <span className="text-xs">Risposto</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">In attesa</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openDetail(ticket); }}
                          className="text-purple-600 hover:text-purple-700 dark:text-purple-400"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Vedi
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
